if (!customElements.get('product-carousel-slider')) {
  class ProductCarouselSlider extends HTMLElement {
    constructor() {
      super();
      this.index = 0;
      this.isAnimating = false;
      this.onResize = this.onResize.bind(this);
      this.onPrev = this.onPrev.bind(this);
      this.onNext = this.onNext.bind(this);
      this.onTransitionEnd = this.onTransitionEnd.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onClickCapture = this.onClickCapture.bind(this);
      this.pointerStartX = 0;
      this.pointerStartY = 0;
      this.pointerId = null;
      this.isPointerDown = false;
      this.hasSwiped = false;
    }

    connectedCallback() {
      this.viewport = this.querySelector('.product-carousel__viewport');
      this.track = this.querySelector('.product-carousel__track');
      this.slides = this.track ? Array.from(this.track.children) : [];
      this.prevButton = this.querySelector('button[name="previous"]');
      this.nextButton = this.querySelector('button[name="next"]');
      this.columnsDesktop = parseInt(this.dataset.columnsDesktop, 10) || 4;
      this.columnsMobile = parseInt(this.dataset.columnsMobile, 10) || 2;
      this.loop = this.dataset.loop !== 'false';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (!this.track || this.slides.length === 0) return;

      this.prevButton?.addEventListener('click', this.onPrev);
      this.nextButton?.addEventListener('click', this.onNext);
      this.track.addEventListener('transitionend', this.onTransitionEnd);
      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this.viewport);
      this.viewport?.addEventListener('pointerdown', this.onPointerDown);
      this.viewport?.addEventListener('pointermove', this.onPointerMove);
      this.viewport?.addEventListener('pointerup', this.onPointerUp);
      this.viewport?.addEventListener('pointercancel', this.onPointerUp);
      this.viewport?.addEventListener('click', this.onClickCapture, true);

      this.onResize();
    }

    disconnectedCallback() {
      this.prevButton?.removeEventListener('click', this.onPrev);
      this.nextButton?.removeEventListener('click', this.onNext);
      this.track?.removeEventListener('transitionend', this.onTransitionEnd);
      this.viewport?.removeEventListener('pointerdown', this.onPointerDown);
      this.viewport?.removeEventListener('pointermove', this.onPointerMove);
      this.viewport?.removeEventListener('pointerup', this.onPointerUp);
      this.viewport?.removeEventListener('pointercancel', this.onPointerUp);
      this.viewport?.removeEventListener('click', this.onClickCapture, true);
      this.resizeObserver?.disconnect();
      clearTimeout(this.animationTimeout);
    }

    get columns() {
      return window.innerWidth >= 750 ? this.columnsDesktop : this.columnsMobile;
    }

    get maxIndex() {
      return Math.max(0, this.slides.length - this.columns);
    }

    get canSlide() {
      return this.slides.length > this.columns;
    }

    onResize() {
      this.setSlideWidths();
      this.slideStep = this.getSlideStep();
      this.setTrackTransition(false);
      this.goTo(this.index, false);
      this.updateButtons();
      this.updateNavLayout();
    }

    setSlideWidths() {
      if (!this.viewport || this.slides.length === 0) return;

      const gap = parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap) || 0;
      const availableWidth = this.viewport.clientWidth;
      const slideWidth = (availableWidth - gap * (this.columns - 1)) / this.columns;
      const widthPx = `${slideWidth}px`;

      this.slides.forEach((slide) => {
        slide.style.flexBasis = widthPx;
        slide.style.width = widthPx;
        slide.style.maxWidth = widthPx;
      });
    }

    updateNavLayout() {
      const frame = this.closest('.product-carousel__frame');
      if (!frame) return;

      frame.classList.toggle('product-carousel__frame--has-nav', this.canSlide);
    }

    getSlideStep() {
      if (this.slides.length < 2) return this.slides[0]?.offsetWidth || 0;

      const first = this.slides[0];
      const second = this.slides[1];
      return Math.round(second.offsetLeft - first.offsetLeft);
    }

    setTrackTransition(enabled) {
      if (!this.track) return;
      this.track.style.transition = enabled && !this.reducedMotion ? 'transform 0.4s ease' : 'none';
    }

    goTo(targetIndex, animate = true) {
      if (!this.track || !this.slideStep) {
        this.isAnimating = false;
        return;
      }

      this.index = targetIndex;
      const shouldAnimate = animate && !this.reducedMotion;
      this.setTrackTransition(shouldAnimate);
      this.track.style.transform = `translate3d(-${this.index * this.slideStep}px, 0, 0)`;
      this.updateButtons();

      clearTimeout(this.animationTimeout);
      if (!shouldAnimate) {
        this.isAnimating = false;
        return;
      }

      this.animationTimeout = setTimeout(() => {
        this.isAnimating = false;
      }, 450);
    }

    onPointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (!this.canSlide) return;

      this.isPointerDown = true;
      this.hasSwiped = false;
      this.pointerId = event.pointerId;
      this.pointerStartX = event.clientX;
      this.pointerStartY = event.clientY;
    }

    onPointerMove(event) {
      if (!this.isPointerDown || event.pointerId !== this.pointerId) return;

      const deltaX = event.clientX - this.pointerStartX;
      const deltaY = event.clientY - this.pointerStartY;

      if (!this.hasSwiped && Math.abs(deltaX) > 12 && Math.abs(deltaX) > Math.abs(deltaY)) {
        this.hasSwiped = true;
        this.viewport?.setPointerCapture?.(event.pointerId);
      }
    }

    onPointerUp(event) {
      if (!this.isPointerDown || event.pointerId !== this.pointerId) return;

      const deltaX = event.clientX - this.pointerStartX;
      this.isPointerDown = false;
      this.pointerId = null;

      if (!this.canSlide || this.isAnimating || Math.abs(deltaX) < 40) return;

      this.hasSwiped = true;

      if (deltaX < 0) {
        this.onNext(event);
      } else {
        this.onPrev(event);
      }
    }

    onClickCapture(event) {
      if (!this.hasSwiped) return;
      event.preventDefault();
      event.stopPropagation();
      this.hasSwiped = false;
    }

    normalizeIndex(index) {
      if (!this.loop || this.maxIndex === 0) {
        return Math.min(Math.max(index, 0), this.maxIndex);
      }

      if (index > this.maxIndex) return 0;
      if (index < 0) return this.maxIndex;
      return index;
    }

    onPrev(event) {
      event.preventDefault();
      if (!this.canSlide || this.isAnimating) return;
      this.isAnimating = true;
      this.goTo(this.normalizeIndex(this.index - 1), true);
    }

    onNext(event) {
      event.preventDefault();
      if (!this.canSlide || this.isAnimating) return;
      this.isAnimating = true;
      this.goTo(this.normalizeIndex(this.index + 1), true);
    }

    onTransitionEnd(event) {
      if (event.propertyName !== 'transform') return;
      this.isAnimating = false;
      clearTimeout(this.animationTimeout);
    }

    updateButtons() {
      if (!this.prevButton || !this.nextButton) return;

      const hide = !this.canSlide;
      this.prevButton.toggleAttribute('hidden', hide);
      this.nextButton.toggleAttribute('hidden', hide);

      if (this.loop || hide) {
        this.prevButton.removeAttribute('disabled');
        this.nextButton.removeAttribute('disabled');
        return;
      }

      this.prevButton.toggleAttribute('disabled', this.index <= 0);
      this.nextButton.toggleAttribute('disabled', this.index >= this.maxIndex);
    }
  }

  customElements.define('product-carousel-slider', ProductCarouselSlider);
}
